import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isBusinessOwner } from '../../../../utils/accessService';
import { buildAvatarImageSource } from '../../../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../../../utils/cropPublicId';
import { useTranslation } from '../../../../utils/useTranslation';
import { BusinessType } from '../../../business/types';
import { IconLink } from '../../../icon/IconLink';
import { Button } from '../../../input/Button';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex, type FlexProps } from '../../../layout/Flex';
import { Avatar } from '../../../media/Avatar';
import { Typography } from '../../../typography/Typography';
import { UserUI } from '../../../users/types';
import { PostUI } from '../../types';
import { PostDetailsCardProps } from '../PostDetailsCard';
import { PostProfileText as Text } from '../styles';

export interface PostTextProfileProps extends FlexProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    user?: UserUI;
    onSubscribe?: PostDetailsCardProps['onSubscribe'];
}

export const PostTextProfile: FC<PostTextProfileProps> = ({
    post,
    index,
    user,
    onSubscribe,
    ...other
}) => {
    const t = useTranslation();

    const handleSubscribe = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onSubscribe?.({ postNumber: index });
            event.stopPropagation();
        },
        [index, onSubscribe],
    );

    const displayName = useMemo(() => {
        if (post.business?.type === BusinessType.BLOGGER && post.business?.displayName) {
            return `${post.business.displayName} `;
        }

        return post.business?.publicId && `${cropPublicId(post.business?.publicId)}`;
    }, [post.business]);

    if (post.ownersData) {
        return (
            <Flex pointerEvents="auto" direction="row" {...other}>
                <Flex direction="row" alignItems="center" width="100%">
                    <Flex
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        textDecoration="none"
                        dataTestId={DataTestId.USER_LOGIN}
                    >
                        <Flex position="relative">
                            <Avatar
                                width={32}
                                title={post.ownersData.author.displayName}
                                src={buildAvatarImageSource(post.ownersData.author.avatarUrl)}
                            />
                            <Box
                                position="absolute"
                                right="-3px"
                                top="-3px"
                                borderRadius="50%"
                                backgroundColor="var(--always-white)"
                                border="solid 2px transparent"
                            >
                                <IconLink size={10} color="var(--always-black)" />
                            </Box>
                        </Flex>
                        <Flex>
                            <Typography
                                variant="caption"
                                weight="medium"
                                color="var(--color-primary)"
                            >
                                {post.ownersData.shop.displayUrl}
                            </Typography>
                            <Typography variant="caption2" color="var(--color-primary)">
                                {post.ownersData.author.displayName}
                            </Typography>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    if (post.business) {
        let profileType = post.business.type === BusinessType.BUSINESS ? t('Бизнес') : t('Автор');

        if (post.business.isService) {
            profileType = t('Услуги');
        }

        return (
            <Flex pointerEvents="auto" direction="row" {...other}>
                <Flex
                    direction="row"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                >
                    <Flex
                        as={Link}
                        to={post.business.url}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        textDecoration="none"
                        dataTestId={DataTestId.BUSINESS_LOGIN}
                        overflow="hidden"
                    >
                        <Avatar
                            width={32}
                            title={post.business.displayName}
                            src={post.business.avatarUrl}
                        />
                        <Flex direction="column" overflow="hidden">
                            <Text weight="medium" maxWidth="150px">
                                {displayName}
                            </Text>
                            <Typography variant="caption2" color="var(--color-secondary)">
                                {profileType}
                            </Typography>
                        </Flex>
                    </Flex>
                    {isBusinessOwner(post.business.id, user) ? null : (
                        <Button
                            onClick={handleSubscribe}
                            variant="filled"
                            color="secondary"
                            size="xs"
                        >
                            <Typography variant="caption2" weight="bold">
                                {post.business?.subscribed ? t('Вы подписаны') : t('Подписаться')}
                            </Typography>
                        </Button>
                    )}
                </Flex>
            </Flex>
        );
    }

    return <Flex pointerEvents="auto" direction="row" {...other} />;
};
